// Generated by GitHub Copilot
// Real API service for Impact Reporting

import { authFetch } from "../utils/authFetch";
import { logError, getUserFriendlyErrorMessage } from "../utils/errorLogging";

const BASE_URL = "/api/v1";

// Generated by GitHub Copilot
// Custom error class for privilege errors
class PrivilegeError extends Error {
  constructor(message) {
    super(message);
    this.name = "PrivilegeError";
    this.statusCode = 403;
  }
}

// Custom error class for duplicate impact report conflicts (HTTP 409)
class DuplicateImpactError extends Error {
  constructor(message) {
    super(message);
    this.name = "DuplicateImpactError";
    this.statusCode = 409;
  }
}

// Generated by GitHub Copilot
// Helper function to handle API responses and privilege errors
async function handleResponse(response) {
  if (!response.ok) {
    // Handle 403 Forbidden - user lacks privilege
    if (response.status === 403) {
      const error = await response.json().catch(() => ({}));
      throw new PrivilegeError(
        error.error || "You do not have permission to access this feature"
      );
    }

    // Handle 409 Conflict - duplicate impact report
    if (response.status === 409) {
      const error = await response.json().catch(() => ({}));
      throw new DuplicateImpactError(
        error.error ||
          "An impact report already exists for this CI in this problem"
      );
    }

    // Handle other errors
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }

  return response.json();
}

class ImpactService {
  /**
   * Get all impacts for a problem
   * @param {string} problemNumber - Problem number
   * @param {object} filters - Optional filters
   * @param {string|string[]} filters.status - Filter by status (active, resolved)
   * @param {object} options - Optional fetch options (e.g. { signal: AbortSignal })
   */
  async getImpacts(problemNumber, filters = {}, options = {}) {
    // Build query parameters
    const queryParams = new URLSearchParams();

    if (filters.status) {
      const statuses = Array.isArray(filters.status)
        ? filters.status.join(",")
        : filters.status;
      queryParams.append("status", statuses);
    }

    // US1812367: Priority filter removed (priority field deprecated)

    const queryString = queryParams.toString();
    const encodedProblemNumber = encodeURIComponent(problemNumber);
    const url = `${BASE_URL}/problems/${encodedProblemNumber}/impacts${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await authFetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      ...options,
    });

    return handleResponse(response);
  }

  /**
   * Get impact summary statistics
   */
  async getSummary(problemNumber) {
    const encodedProblemNumber = encodeURIComponent(problemNumber);
    const response = await authFetch(
      `${BASE_URL}/problems/${encodedProblemNumber}/impacts/summary`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return handleResponse(response);
  }

  /**
   * Get a single impact by ID
   */
  async getImpact(problemNumber, impactID) {
    const encodedProblemNumber = encodeURIComponent(problemNumber);
    const encodedImpactID = encodeURIComponent(impactID);
    const response = await authFetch(
      `${BASE_URL}/problems/${encodedProblemNumber}/impacts/${encodedImpactID}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return handleResponse(response);
  }

  /**
   * Create a new impact
   */
  async createImpact(problemNumber, impactData) {
    const encodedProblemNumber = encodeURIComponent(problemNumber);
    const response = await authFetch(
      `${BASE_URL}/problems/${encodedProblemNumber}/impacts`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ impact: impactData }),
      }
    );

    return handleResponse(response);
  }

  /**
   * Update an existing impact
   */
  async updateImpact(problemNumber, impactID, impactData) {
    const encodedProblemNumber = encodeURIComponent(problemNumber);
    const encodedImpactID = encodeURIComponent(impactID);
    const response = await authFetch(
      `${BASE_URL}/problems/${encodedProblemNumber}/impacts/${encodedImpactID}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ impact: impactData }),
      }
    );

    return handleResponse(response);
  }

  /**
   * Delete an impact
   */
  async deleteImpact(problemNumber, impactID) {
    const encodedProblemNumber = encodeURIComponent(problemNumber);
    const encodedImpactID = encodeURIComponent(impactID);
    const response = await authFetch(
      `${BASE_URL}/problems/${encodedProblemNumber}/impacts/${encodedImpactID}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return handleResponse(response);
  }

  /**
   * Get impacts by brand
   */
  async getImpactsByBrand(brand) {
    const response = await authFetch(
      `${BASE_URL}/impacts/by-brand/${encodeURIComponent(brand)}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return handleResponse(response);
  }

  /**
   * Get brands from centralized brand API endpoint
   */
  async getBrands() {
    // Generated by GitHub Copilot
    const response = await authFetch(`${BASE_URL}/brands`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.error || `Failed to fetch brands: ${response.statusText}`
      );
    }

    // API returns { brands: [...], count: n }
    const data = await response.json();

    // Extract brands array from response
    const brands = data.brands || [];

    // Validate response has brands array
    if (!Array.isArray(brands)) {
      console.error("Unexpected response format for brands:", data);
      return [];
    }

    // Extract just the brand names from the brand objects
    return brands.map((brand) => brand.name);
  }

  /**
   * Generated by GitHub Copilot - US1803046
   * Check if an impact report already exists for a given CI (Configuration Item) within a specific problem
   * Returns the existing impact if found, or null if no duplicate exists
   * @param {string} problemNumber - Problem number to check within
   * @param {string} ci - Configuration Item identifier
   * @returns {Promise<object|null>} - Existing impact record or null
   */
  async checkDuplicateByCI(problemNumber, ci) {
    if (!problemNumber || !ci || ci.trim() === "") {
      return null;
    }

    // Normalize CI to uppercase for case-insensitive comparison
    const normalizedCI = ci.trim().toUpperCase();
    const encodedProblemNumber = encodeURIComponent(problemNumber);
    const encodedCI = encodeURIComponent(normalizedCI);
    const url = `${BASE_URL}/problems/${encodedProblemNumber}/impact/${encodedCI}`;

    try {
      const response = await authFetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      // 404 means no duplicate found - this is not an error
      if (response.status === 404) {
        return null;
      }

      // Handle other errors (403, 500, etc)
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error(
          "ImpactService.checkDuplicateByCI - Error:",
          response.status,
          error
        );
        throw new Error(
          error.error || `Duplicate check failed: ${response.statusText}`
        );
      }

      // Success - duplicate exists
      const impact = await response.json();
      return impact;
    } catch (error) {
      logError('impactService.checkDuplicateByCI', error, { ci });
      // Re-throw with user-friendly message
      throw new Error(getUserFriendlyErrorMessage(error));
    }
  }
}

// Export singleton instance and error classes
export const impactService = new ImpactService();
export { DuplicateImpactError, PrivilegeError };
